// Выписки банков: узнать отправителя по подписям, подсказки для «К разбору», ключ повторной строки.

// Подпись для сравнения: строчные, ё → е, только буквы и цифры через пробел.
export function normSig(s: string | null | undefined): string {
  return String(s ?? '').toLowerCase().replace(/ё/g, 'е').replace(/[^a-zа-я0-9]+/g, ' ').trim();
}

// Грубая транслитерация в латиницу: «Ольга Д.» и «OLGA D.» совпадут.
const TRANSLIT: Readonly<Record<string, string>> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ж: 'zh', з: 'z', и: 'i', й: 'i', к: 'k', л: 'l', м: 'm', н: 'n',
  о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y',
  ь: '', э: 'e', ю: 'yu', я: 'ya',
};
export function translit(s: string | null | undefined): string {
  return normSig(s).replace(/[а-я]/g, (ch) => TRANSLIT[ch] ?? ch);
}

// «LENA H.; Лена» → ['lena h', 'лена']
export function splitSigs(s: string | null | undefined): string[] {
  return String(s ?? '').split(';').map((x) => normSig(x)).filter(Boolean);
}

// 2 — точное совпадение (в т.ч. через транслит), 1 — подпись входит в отправителя, 0 — нет.
export function matchSig(sender: string, sigs: readonly string[]): 0 | 1 | 2 {
  const n = normSig(sender), t = translit(sender);
  for (const g of sigs) {
    if (!g) continue;
    if (n === g || t === translit(g)) return 2;
    if (g.length >= 4 && (n.includes(g) || t.includes(translit(g)))) return 1;
  }
  return 0;
}

export interface SenderMatch { kind: 'excluded' | 'client' | 'group'; id: string; name: string; score: number }

// Кто это по подписи: сначала исключения («не клиенты»), потом клиенты и группы-проекты.
export function whoIsSender(sender: string, clients: ReadonlyArray<{ id: string; name: string; bankNames?: string | null }>,
  groups: ReadonlyArray<{ id: string; name: string; bankNames?: string | null }>,
  exclusions: ReadonlyArray<{ sig: string; who?: string | null }> = []): SenderMatch | null {
  for (const ex of exclusions) {
    if (matchSig(sender, [ex.sig]) === 2 || (normSig(ex.sig).length >= 4 && matchSig(sender, [ex.sig]))) {
      return { kind: 'excluded', id: '', name: ex.who ?? '', score: 2 };
    }
  }
  let best: SenderMatch | null = null;
  for (const c of clients) {
    const sc = matchSig(sender, splitSigs(c.bankNames));
    if (sc && (!best || sc > best.score)) best = { kind: 'client', id: c.id, name: c.name, score: sc };
  }
  for (const g of groups) {
    const sc = matchSig(sender, splitSigs(g.bankNames));
    if (sc && (!best || sc > best.score)) best = { kind: 'group', id: g.id, name: g.name, score: sc };
  }
  return best;
}

export interface Hint { id: string; name: string; why: string; weight: number }

// Подсказка для «К разбору»: похожее имя или сумма, равная цене клиента (или кратная ей).
export function hintFor(row: { amount: number; cur: string; sender: string },
  clients: ReadonlyArray<{ id: string; name: string; cur?: string | null; price?: number | null; archived?: boolean }>):
  Hint[] {
  const hints: Hint[] = [];
  for (const c of clients) {
    if (c.archived) continue;
    const reasons: string[] = [];
    const price = Number(c.price) || 0;
    if (c.cur === row.cur && price > 0) {
      const k = row.amount / price;
      if (Math.abs(k - Math.round(k)) < 0.001 && k >= 1 && k <= 12) {
        reasons.push(k === 1 ? 'сумма равна цене' : 'сумма = ' + Math.round(k) + ' × цена');
      }
    }
    const first = normSig(c.name).split(' ')[0] ?? '';
    if (first.length >= 3 && (normSig(row.sender).includes(first) || translit(row.sender).includes(translit(first)))) {
      reasons.push('похоже на имя');
    }
    if (reasons.length) hints.push({ id: c.id, name: c.name, why: reasons.join(', '), weight: reasons.length });
  }
  return hints.sort((a, b) => b.weight - a.weight).slice(0, 3);
}

// Одна и та же строка выписки, загруженная дважды, получает тот же ключ.
export function dupKey(bank: string, date: string, amount: number, cur: string, sender: string): string {
  return [bank, date, (Math.round(Number(amount) * 100) / 100).toFixed(2), cur, normSig(sender).slice(0, 60)].join('|');
}
